import classNames from "classnames"
import dayjs from "dayjs"
import { truncate } from "lodash"
import useQueryParams from "magik-react-hooks/useRouterDebounceQueryParams"
import { useContext, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap"
import Button from "../../../components/Button"
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal"
import Icon from "../../../components/Icon"
import Layout from "../../../components/Layout"
import Loader from "../../../components/Loader/Loader"
import LongBorder from "../../../components/LongBorder"
import Pagination from "../../../components/Pagination/Pagination"
import Table from "../../../components/Table"
import { TrackerLandmarks } from "../../../components/TrackerLandmarks"
import { ProjectContext } from "../../../context"
import { useDeadlines } from "../../../hooks/deadline"
import useModalTrigger from "../../../hooks/useModalTrigger"
import ProjectBottomBarContent from "../ProjectBottomBarContent"
import ProjectTabBarContent from "../ProjectTabBarContent"
import { EditDeadlineModal } from "./EditDeadlineModal"

export function ProjectDeadlines() {
  const { t } = useTranslation(["translation", "tab", "action"])

  const [
    ,
    setQueryParams,
    { page: debPage, search: debSearch },
    ,
  ] = useQueryParams({
    page: {
      encode: (intValue) => intValue.toString(10),
      decode: (strValue) => (strValue ? parseInt(strValue, 10) : 1),
    },
  })

  const [{ project }] = useContext(ProjectContext)

  const filter = useMemo(
    () => ({
      project: project.id,
      page: debPage,
      search: debSearch,
    }),
    [debPage, debSearch, project.id]
  )

  const [
    { deadlines, pageCount, hasNext, hasPrev },
    { create, update, remove, run: refresh },
  ] = useDeadlines(filter)

  const [editModalState, editModalActions] = useModalTrigger()
  const [removeModalState, removeModalActions] = useModalTrigger()

  const columns = useMemo(() => {
    return [
      {
        label: t("field:deadline.date"),
        name: "date",
        size: 2,
        render: (selectedDatum, index, datum) => {
          return (
            <span>
              {dayjs(selectedDatum).format("DD/MM/YYYY")}
              {dayjs(datum.date).isBefore(dayjs()) &&
                datum.completion_percent < 100 &&
                " 🔴"}
            </span>
          )
        },
      },
      {
        label: t("field:deadline.description"),
        name: "description",
        size: 7,
      },
      {
        label: t("field:deadline.completion_percent"),
        name: "completion_percent",
        size: 2,
        render: (selectedDatum, index, datum) => {
          return (
            <span>
              {selectedDatum ? selectedDatum + " %" : ""}
              {dayjs(datum.date).isBefore(dayjs()) &&
                selectedDatum < 100 &&
                " 🔴"}
            </span>
          )
        },
      },
      {
        label: t("actions"),
        name: "actions",
        size: 1,
        render: (selectedDatum, colName, datum) => {
          return (
            <div className="d-flex flex-row justify-content-between align-items-center w-100">
              <div className={"action-icon-container-primary-hover"}>
                <Icon
                  name="edit"
                  title={t("action:edit_project_task")}
                  className="pointer text-dark"
                  onClick={() => {
                    editModalActions.open(datum)
                  }}
                />
              </div>
              <div className={"action-icon-container-primary-hover"}>
                <UncontrolledDropdown>
                  <DropdownToggle caret={false} tag={"span"}>
                    <Icon
                      role="button"
                      name="vdots"
                      className="text-dark pointer"
                      title={t("action:other_options")}
                      placement="right"
                    />
                  </DropdownToggle>
                  <DropdownMenu
                    right
                    modifiers={{ offset: { offset: "0, 12" } }}
                    className="border-primary"
                  >
                    {!datum.archived && (
                      <DropdownItem
                        className={classNames(
                          "text-capitalize px-0",
                          "dropdown-item-primary-active"
                        )}
                      >
                        <div
                          className="d-flex flex-row align-items-center mx-4"
                          onClick={() => {
                            removeModalActions.open(datum)
                          }}
                        >
                          <Icon
                            name="delete"
                            className={classNames("pointer mr-4")}
                          />
                          <span>{t("action:delete")}</span>
                        </div>
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            </div>
          )
        },
      },
    ]
  }, [editModalActions, removeModalActions, t])

  return (
    <Layout>
      <div className="container-fluid pt-5 px-page pb-page-bottom">
        <div className="d-flex flex-row justify-content-between align-items-center mb-6">
          <h1 className="text-primary">{t("tab:project.deadline")}</h1>
          <div>
            <Button
              className="d-inline-flex align-items-center px-5 mr-5"
              onClick={() => {
                editModalActions.open({ project: project.id })
              }}
            >
              <Icon
                name="plus-rounded"
                className="mr-2"
                height={16}
                width={16}
              />
              {t("action:new_project_deadline")}
            </Button>
          </div>
        </div>

        {(!project || !deadlines) && <Loader />}
        {project && deadlines && (
          <>
            <LongBorder topBar="tabbar" />
            <Table
              data={deadlines}
              columns={columns}
              stickyHeader
              stickyTopBar="tabbar"
              className="pb-8"
              valueCheckFn={(val) => val !== undefined && val !== null}
            />
            {deadlines.length > 0 && (
              <Pagination
                current={debPage}
                pageCount={pageCount}
                goToPage={(page) => {
                  setQueryParams({ page })
                  window.scrollTo(0, 10)
                }}
                hasNext={hasNext}
                hasPrev={hasPrev}
              />
            )}
          </>
        )}
        {editModalState.value && (
          <EditDeadlineModal
            isOpen={editModalState.isOpen}
            deadline={editModalState.value}
            onClosed={editModalActions.onClose}
            toggle={editModalActions.toggle}
            onSave={(result) => {
              if (result.id) {
                return update
                  .onSuccess(() => {
                    editModalActions.close()
                  })
                  .asPromise(result.id, result)
              } else {
                return create
                  .onSuccess(() => {
                    editModalActions.close()
                  })
                  .asPromise(result)
              }
            }}
          />
        )}
        {removeModalState.value && (
          <ConfirmDeleteModal
            isOpen={removeModalState.isOpen}
            toggle={removeModalActions.toggle}
            onClosed={removeModalActions.onClose}
            item={truncate(removeModalState.value.description, 20)}
            onConfirm={() => {
              remove
                .onSuccess(() => {
                  refresh(filter)
                })
                .run(removeModalState.value.id)
            }}
          />
        )}
      </div>
      <Layout.TabBar>
        <ProjectTabBarContent />
      </Layout.TabBar>
      <Layout.BottomBar className="border-top border-separator">
        <ProjectBottomBarContent />
      </Layout.BottomBar>
      <Layout.FirstLevelNavi>
        <TrackerLandmarks />
      </Layout.FirstLevelNavi>
    </Layout>
  )
}
